// mapInteractions.js
import { Collection } from 'ol';
import { Draw, Select, DragBox, Modify, Snap, Translate, Interaction, DragPan } from 'ol/interaction';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import { Point } from 'ol/geom';
import { Circle, Fill, Stroke, Style } from 'ol/style';
import { containsCoordinate } from 'ol/extent';

/**
 * Reads the current viewInformation snapshot from an OpenLayers map.
 *
 * - From the OL View (native): center, resolution, min/maxResolution.
 * - From map custom properties (set earlier by the MapWrapper sync effect, after
 *   computeViewInformation): x_left_right, y_bottom_up, global_extent.
 *   Falls back to view.calculateExtent if global_extent was never set.
 *
 * Does not initialize anything — use computeViewInformation for the initial extent/center/zoom.
 */
export function getViewInformation(map) {
  if (!map) return null;
  const view = map.getView();
  const x_left_right = map.get('x_left_right') ?? true;
  const y_bottom_up = map.get('y_bottom_up') ?? true;
  return {
    x_left_right,
    y_bottom_up,
    extent: map.get('global_extent') ?? (view && map.getSize() ? view.calculateExtent(map.getSize()) : null),
    center: view ? view.getCenter() : null,
    resolution: view ? view.getResolution() : null,
    minResolution: view ? view.getMinResolution() : null,
    maxResolution: view ? view.getMaxResolution() : null,
  };
}

/**
 * Converts OpenLayers coordinates to reference coordinates on the fly using viewInformation or map
 */
export function openlayer_to_reference_coordinates(coord, mapOrViewInformation) {
  const info = mapOrViewInformation?.getView ? getViewInformation(mapOrViewInformation) : mapOrViewInformation;
  const x_left_right = info?.x_left_right ?? true;
  const y_bottom_up = info?.y_bottom_up ?? true;
  return [
    x_left_right ? coord[0] : -coord[0],
    y_bottom_up ? coord[1] : -coord[1]
  ];
}

// Axis sign flip is involutive — same transform both ways
export const reference_to_openlayer_coordinates = openlayer_to_reference_coordinates;

/**
 * Initializes viewInformation (extent, center, zoom range + axis conventions) from app data — not from the map.
 *
 * - With orthophoto layers: axes + extent + zoom range from TFW / sizeInPixels.
 * - Without ortho: extent from annotation centers (default axes); zoom derived from that span.
 *
 * Result is later written onto the map (custom props + View) by MapWrapper; after that,
 * getViewInformation can read it back from the map.
 */
export function computeViewInformation(stateLayers, annotations) {
  const orthoLayers = stateLayers.filter((l) => l.type === "orthophoto");

  // OpenLayers expects x→right and y→up. Ortho TFW can flip either axis; detect that here.
  let x_left_right = true;
  let y_bottom_up = true;
  orthoLayers.forEach(({ tfw_adbecf }) => {
    if (tfw_adbecf?.[0] < 0) x_left_right = false;
    if (tfw_adbecf?.[3] > 0) y_bottom_up = false;
  });
  const axes = { x_left_right, y_bottom_up };

  let pts = [];
  let minRes = Infinity;
  let maxRes = 0;

  if (orthoLayers.length > 0) {
    orthoLayers.forEach(({ sizeInPixels, tfw_adbecf }) => {
      if (!tfw_adbecf || tfw_adbecf.length < 6 || !sizeInPixels) return;
      const [a, b, d, e, c, f] = tfw_adbecf;
      const [w, h] = sizeInPixels;
      // Origin + opposite corner: used to compute the extent of the orthophoto
      pts.push(reference_to_openlayer_coordinates([c, f], axes));
      pts.push(reference_to_openlayer_coordinates([a * w + d * h + c, b * w + e * h + f], axes));
      // Zoom range from pixel size (max ≈ whole ortho ~100px; min ≈ 2× pixel)
      maxRes = Math.max(maxRes, Math.abs((w * a) / 100), Math.abs((h * d) / 100));
      minRes = Math.min(minRes, 2 * Math.abs(a));
    });
  } else {
    // No ortho: extent from annotation geometries (fallback: center)
    annotations.forEach((a) => {
      const coords = a.geometry?.coordinates;
      if (a.geometryName === "point" && coords) {
        pts.push(reference_to_openlayer_coordinates(coords, axes));
      } else if ((a.geometryName === "line" || a.geometryName === "polygon") && Array.isArray(coords)) {
        coords.forEach((c) => pts.push(reference_to_openlayer_coordinates(c, axes)));
      } else if (a.position?.center) {
        const { x, y } = a.position.center;
        pts.push(reference_to_openlayer_coordinates([x.value, y.value], axes));
      }
    });
  }

  let extent = pts.length
    ? [
        Math.min(...pts.map((p) => p[0])),
        Math.min(...pts.map((p) => p[1])),
        Math.max(...pts.map((p) => p[0])),
        Math.max(...pts.map((p) => p[1])),
      ]
    : [-1000, -1000, 1000, 1000];

  const padX = Math.max((extent[2] - extent[0]) / 4, 1000);
  const padY = Math.max((extent[3] - extent[1]) / 4, 1000);
  extent = [extent[0] - padX, extent[1] - padY, extent[2] + padX, extent[3] + padY];

  const span = Math.max(extent[2] - extent[0], extent[3] - extent[1], 2000);
  if (!Number.isFinite(maxRes) || maxRes <= 0) maxRes = span / 100;
  if (!Number.isFinite(minRes) || minRes === Infinity || minRes <= 0) minRes = maxRes / 100;

  return {
    x_left_right,
    y_bottom_up,
    extent,
    center: [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2],
    resolution: maxRes,
    minResolution: minRes / 4,
    maxResolution: maxRes,
  };
}

export const initMapInteractions = (
	map,
	dispatch,
	setNewGeometries,
	setSelectionBoxExtent,
	styleFunction,
	updateAnnotationState,
	setDisplayedPhoto,
	selectMultipleAnnotations,
	shiftKeyOnly,
	featuresLayer,
	selectedFeaturesRef,
	setAnnotationsForGeometryEdit,
	annotationsRef,
	selectedAnnotationsRef,
	setTranslateOffsetDraft,
	setTextLabelDraft,
	textLabelDraftRef,
	selectTargetRef,
) => {
	const interactions = {};

	interactions.drawLine = initDrawLineInteraction(map, dispatch, setNewGeometries, styleFunction, updateAnnotationState);
	interactions.drawPolygon = initDrawPolygonInteraction(map, dispatch, setNewGeometries, styleFunction, updateAnnotationState);
	interactions.drawPoint = initDrawPointInteraction(map, dispatch, setNewGeometries, styleFunction, updateAnnotationState);
	interactions.drawSquare = initDrawSquareInteraction(map, dispatch, setNewGeometries, styleFunction, updateAnnotationState);
	interactions.selectSingleClick = initSelectSingleClickInteraction(map, dispatch, setDisplayedPhoto, selectMultipleAnnotations, selectedFeaturesRef, selectTargetRef);
	interactions.selectionBox = initSelectionBoxInteraction(map, setSelectionBoxExtent, shiftKeyOnly);
	interactions.modify = initModifyInteraction(map, dispatch, setNewGeometries, updateAnnotationState, selectedFeaturesRef);
	interactions.partialDrawPostVertex = initCustomRedrawPostVertexInteraction(map, dispatch, featuresLayer, setNewGeometries, selectedFeaturesRef);
	interactions.partialDrawPreVertex = initCustomRedrawPreVertexInteraction(map, dispatch, featuresLayer, setNewGeometries, selectedFeaturesRef);
	interactions.translate = initTranslationInteraction(map, dispatch, updateAnnotationState, setNewGeometries, featuresLayer, setAnnotationsForGeometryEdit);
	interactions.translateByTwoClicks = initTranslateByTwoClicksInteraction(map, dispatch, updateAnnotationState, setTranslateOffsetDraft);
	interactions.removePointsInExtent = initRemovePointsInExtentInteraction(
		map, dispatch, setNewGeometries, setAnnotationsForGeometryEdit, annotationsRef, selectedAnnotationsRef,
	);
	interactions.textLabel = initTextLabelInteraction(map, dispatch, setTextLabelDraft, textLabelDraftRef);

	// default interaction in annotate is selection
	for (const [key, interaction] of Object.entries(interactions)) {
		const isActive = key === 'selectSingleClick' || key === 'selectionBox';
		interaction.setActive(isActive);
	}

	return interactions;
};

// 1. LineString drawing
const initDrawLineInteraction = (map, dispatch, setNewGeometries, styleFunction, updateAnnotationState) => {
	const drawLine = new Draw({
		type: 'LineString',
		geometryName: "line",
		style: styleFunction("line")
	});

	drawLine.on('drawstart', () => {
		dispatch(updateAnnotationState("DrawOnGoing"));
	});

	drawLine.on('drawend', (e) => {
		const feature = e.feature;
		const c = feature.getGeometry().flatCoordinates;
		let polyCoord = [];
		let bary = [0, 0];

		for (let i = 0; i < c.length; i += 2) {
			polyCoord.push([c[i], c[i + 1]]);
			bary[0] += parseFloat(c[i]);
			bary[1] += parseFloat(c[i + 1]);
		}

		bary = [bary[0] / polyCoord.length, bary[1] / polyCoord.length];

		const geometryToBeSaved = {
			geometry: {
				type: "LineString",
				coordinates: polyCoord.map((c) => openlayer_to_reference_coordinates(c, map))
			},
			geometryName: "line"
		};

		dispatch(setNewGeometries([geometryToBeSaved]));
	});

	map.addInteraction(drawLine);
	return drawLine;
};

// 2. Polygon drawing
const initDrawPolygonInteraction = (map, dispatch, setNewGeometries, styleFunction, updateAnnotationState) => {
	const drawPolygon = new Draw({
		type: 'Polygon',
		geometryName: "polygon",
		style: styleFunction("polygon")
	});

	drawPolygon.on('drawstart', () => {
		dispatch(updateAnnotationState("DrawOnGoing"));
	});

	drawPolygon.on('drawend', (e) => {
		const feature = e.feature;
		const c = feature.getGeometry().flatCoordinates;
		let polyCoord = [];

		for (let i = 0; i < c.length; i += 2) {
			polyCoord.push([c[i], c[i + 1]]);
		}

		polyCoord.push(polyCoord[0]); // Closing the polygon

		const geometryToBeSaved = {
			geometry: {
				type: "Polygon",
				coordinates: polyCoord.map((c) => openlayer_to_reference_coordinates(c, map))
			},
			geometryName: "polygon"
		};

		dispatch(setNewGeometries([geometryToBeSaved]));
	});

	map.addInteraction(drawPolygon);
	return drawPolygon;
};

// 3. Point drawing
const initDrawPointInteraction = (map, dispatch, setNewGeometries, styleFunction, updateAnnotationState) => {
	const drawPoint = new Draw({
		type: 'Point',
		geometryName: "point",
		style: styleFunction("point")
	});

	drawPoint.on('drawstart', () => {
		dispatch(updateAnnotationState("DrawOnGoing"));
	});

	drawPoint.on('drawend', (e) => {
		const feature = e.feature;
		const c = feature.getGeometry().flatCoordinates;

		const geometryToBeSaved = {
			geometry: {
				type: "Point",
				coordinates: openlayer_to_reference_coordinates(c, map)
			},
			geometryName: "point"
		};

		dispatch(setNewGeometries([geometryToBeSaved]));
	});

	map.addInteraction(drawPoint);
	return drawPoint;
};

// 4. Square drawing
const initDrawSquareInteraction = (map, dispatch, setNewGeometries, styleFunction, updateAnnotationState) => {
  const drawSquare = new Draw({
    type: 'Polygon',
    geometryName: "square",
    style: styleFunction("square")
  });

  drawSquare.on('drawstart', () => {
    dispatch(updateAnnotationState("DrawOnGoing"));
  });

  drawSquare.on('drawend', (e) => {
    const feature = e.feature;
    const c = feature.getGeometry().flatCoordinates;

    const geometryToBeSaved = {
      geometry: {
        type: "Polygon",
        coordinates: openlayer_to_reference_coordinates(c, map)
      },
      geometryName: "square"
    };

    dispatch(setNewGeometries([geometryToBeSaved]));
  });

  map.addInteraction(drawSquare);
  return drawSquare;
};

// 5. Selection interaction
const initSelectSingleClickInteraction = (map, dispatch, setDisplayedPhoto, selectMultipleAnnotations, selectedFeaturesRef, selectTargetRef) => {
  const selectSingleClick = new Select({
    style: null,
    hitTolerance: 5,
    // trigger by the select tool toggle
    // used to hit either annotations or text labels, not both
    filter: (feature) => {
      const a = feature.getProperties()?.annotation;
      if (!a?.id) return true;
      const isText = Boolean(a.text?.value);
      const wantText = selectTargetRef.current === "text";
      return wantText ? isText : !isText;
    },
  });

  selectSingleClick.on('select', (e) => {
    const features = e.target.getFeatures().getArray();

    if (features.length === 1 && features[0].getProperties().photo != null) {
      dispatch(setDisplayedPhoto(features[0].getProperties().photo));
      selectSingleClick.getFeatures().clear();
      dispatch(selectMultipleAnnotations([]));
    } else {
      let selectedAnnotationsIds = features.map(f => f.getProperties().annotation.id);
      let counter = selectedAnnotationsIds.reduce((acc, aid) => {
        acc[aid] = (acc[aid] || 0) + 1;
        return acc;
      }, {});
      selectedAnnotationsIds = selectedAnnotationsIds.filter(aid => counter[aid] === 1);
      dispatch(selectMultipleAnnotations(selectedAnnotationsIds));
    }

    if (features.length > 0) {
      selectedFeaturesRef.current = [features[0]];
    } else {
      selectedFeaturesRef.current = [];
    }
  });

  map.addInteraction(selectSingleClick);
  return selectSingleClick;
};


// 6. Selection with polygon using DragBox
const initSelectionBoxInteraction = (map, setSelectionBoxExtent, shiftKeyOnly) => {
  const selectionBox = new DragBox({ condition: shiftKeyOnly });

  selectionBox.on('boxend', () => {
    const extent = selectionBox.getGeometry().getExtent();
    setSelectionBoxExtent(extent.slice());
  });

  
  map.addInteraction(selectionBox);
  return selectionBox;
};

// 7. Edit geometry interaction
// https://openlayers.org/en/latest/examples/modify-features.html
const initModifyInteraction = (map, dispatch, setNewGeometries, updateAnnotationState, selectedFeaturesRef) => {
  const modify = new Modify({
    features: new Collection(),
  });

  let currentFeature = null;
  let keyListener = null;

  modify.on('modifystart', () => {
    dispatch(updateAnnotationState("AnnotationModifyOnGoing"));
  })

  modify.on('modifyend', () => {

    let savedAnnotation = null;
    if (selectedFeaturesRef.current[0]) {
      const feature = selectedFeaturesRef.current[0];
      const props = feature.getProperties();

      if (props.annotation) {
        savedAnnotation = props.annotation;
      }
      currentFeature = feature;
      selectedFeaturesRef.current = [feature];
    }

    const geometry = currentFeature.getGeometry();
    const type = geometry.getType().toLowerCase();
    const coordinates = geometry.getCoordinates();

    let newCoords = null;
    let geometryName = type;
    if (type === "point") {
      newCoords = openlayer_to_reference_coordinates(coordinates, map);
    } else if (type === "linestring") {
      geometryName = "line";
      newCoords = coordinates.map(c => openlayer_to_reference_coordinates(c, map));
    } else if (type === "polygon") {
      newCoords = coordinates[0].map(c => openlayer_to_reference_coordinates(c, map));
    }
    const geometryToBeSaved = {
      geometry: {
        type: geometry.getType(),
        coordinates: newCoords,
      },
      geometryName: geometryName,
    };
    dispatch(setNewGeometries([geometryToBeSaved]));
  });

  map.addInteraction(modify);

  return modify;
};



// 8. Draw from a given existing geometry pre selected vertex
const initCustomRedrawPreVertexInteraction = (map, dispatch, source, setNewGeometries, selectedFeaturesRef) => {
  const src = (source && typeof source.getSource === 'function') ? source.getSource() : source;
  const draw = new Draw({
    source: src,
    type: 'LineString',
  });

  let existingPartAfterClick = [];

  draw.on("drawstart", (event) => {

    // can't redraw on anything else than linestring
    if (selectedFeaturesRef.current[0].getGeometry().getType() !== 'LineString') {
      alert("Partial Redraw works only on lines, please select another tool")
      draw.abortDrawing();
    }

    const feature = selectedFeaturesRef.current[0];
    const featureGeometry = feature.getGeometry();
    const coords = featureGeometry.getCoordinates();

    const clickedCoord = event.feature.getGeometry().getCoordinates()[0];
    const tolerance = map.getView().getResolution() * 15;

    let vertexFound = false;
    let cutIndex = -1;

    for (let i = 0; i < coords.length; i++) {
      const vertex = coords[i];
      const dx = vertex[0] - clickedCoord[0];
      const dy = vertex[1] - clickedCoord[1];
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < tolerance) {
        vertexFound = true;
        cutIndex = i;
        break;
      }
    }

    if (!vertexFound) {
      draw.abortDrawing();
      console.log("vertex not found");
      return;
    }

    // keeping track of existing geometry post vertices removal
    existingPartAfterClick = coords.slice(cutIndex + 1);
  });

  draw.on("drawend", (event) => {
    const feature = event.feature;
    const drawnCoords = feature.getGeometry().getCoordinates();

    if (feature.getGeometry().getType().toLowerCase() != "linestring")
      draw.abortDrawing();

    const newCoords = drawnCoords; 
    const reversedCoords = newCoords.reverse();
    const cleanedOld = existingPartAfterClick.slice(); 
    const finalCoords = reversedCoords.concat(cleanedOld);
    

    const convertedCoords = finalCoords.map((c) =>
      openlayer_to_reference_coordinates(c, map)
    );

    const geometryToBeSaved = {
      geometry: {
        type: feature.getGeometry().getType(),
        coordinates: convertedCoords,
      },
      geometryName: "line"
    };

    dispatch(setNewGeometries([geometryToBeSaved]));
    selectedFeaturesRef.current = [];
  });

  map.addInteraction(draw);
  return draw;
};




// 9. Draw from a given existing geometry post selected vertex
const initCustomRedrawPostVertexInteraction = (map, dispatch, source, setNewGeometries, selectedFeaturesRef) => {
  const src = (source && typeof source.getSource === 'function') ? source.getSource() : source;
  const draw = new Draw({
    source: src,
    type: 'LineString',
  });

  let existingPartBeforeClick = [];

  draw.on("drawstart", (event) => {
    // can't redraw on anything else than linestring
    if (selectedFeaturesRef.current[0].getGeometry().getType() !== 'LineString') {
      alert("Partial Redraw works only on lines, please select another tool")
      draw.abortDrawing();
    }

    const feature = selectedFeaturesRef.current[0];
    const featureGeometry = feature.getGeometry();
    const coords = featureGeometry.getCoordinates();
    const clickedCoord = event.feature.getGeometry().getCoordinates()[0];
    const tolerance = map.getView().getResolution() * 15;

    let vertexFound = false;
    let cutIndex = -1;

    for (let i = 0; i < coords.length; i++) {
      const vertex = coords[i];
      const dx = vertex[0] - clickedCoord[0];
      const dy = vertex[1] - clickedCoord[1];
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < tolerance) {
        vertexFound = true;
        cutIndex = i;
        break;
      }
    }

    if (!vertexFound) {
      draw.abortDrawing();
      console.log("vertex not found");
      return;
    }

    // keeping track of geometry post vertices removal
    existingPartBeforeClick = coords.slice(0, cutIndex + 1);
  });

  draw.on("drawend", (event) => {
    const feature = event.feature;
    const drawnCoords = feature.getGeometry().getCoordinates();
  
    let finalCoords;
    let type = feature.getGeometry().getType().toLowerCase();
  
    if (type === "linestring") {
      const newCoords = drawnCoords;
      finalCoords = finalCoords = existingPartBeforeClick.concat(newCoords.slice(1));
    } else if (type === "polygon") {
      const newCoords = drawnCoords[0];
      const joined = existingPartBeforeClick.concat(newCoords.slice(1));
      finalCoords = [joined];
    } else {
      finalCoords = drawnCoords;
    }

    const convertedCoords = (type === "polygon" ? finalCoords[0] : finalCoords).map((c) =>
      openlayer_to_reference_coordinates(c, map)
    );
  
    const geometryToBeSaved = {
      geometry: {
        type: feature.getGeometry().getType(),
        coordinates: convertedCoords,
      },
      geometryName: type === "linestring" ? "line" : type,
    };

    // saving newGeometry in DB
    dispatch(setNewGeometries([geometryToBeSaved]));
    selectedFeaturesRef.current = [];
  });
  

  map.addInteraction(draw);
  return draw;
};




// 10. Moving one or more annotations with a translation
// https://openlayers.org/en/latest/examples/translate-features.html
const initTranslationInteraction = (map, dispatch, updateAnnotationState, setNewGeometries, source, setAnnotationsForGeometryEdit) => {
  const src = (source && typeof source.getSource === 'function') ? source.getSource() : source;
  const translate = new Translate({
    source: src,
    hitTolerance: 5,
  });

  // starting to drag an annotation
  translate.on('translatestart', () => {
    dispatch(updateAnnotationState("AnnotationsTranslationOnGoing"));
  })

  // dropping the annotation, triggering save in the DB
  translate.on('translateend', (e) => {
    //  get features
    const features = e.features.getArray();
    if (features.length === 0) return;

    // get translate event targeted feature 
    const feature = features[0];
    const props = feature.getProperties();

    // Safer extraction of the annotation id; do not trust injected objects
    const annotationId = props?.annotation?.id;
    if (!annotationId) return; // no valid annotation target => abort

    const geometry = feature.getGeometry();
    const type = geometry.getType().toLowerCase();
    const coordinates = geometry.getCoordinates();

    let newCoords = null;
    let geometryName = type;
    if (type === "point") {
      newCoords = openlayer_to_reference_coordinates(coordinates, map);
    } else if (type === "linestring") {
      geometryName = "line";
      newCoords = coordinates.map(c => openlayer_to_reference_coordinates(c, map));
    } else if (type === "polygon") {
      newCoords = coordinates[0].map(c => openlayer_to_reference_coordinates(c, map));
    }
    const geometryToBeSaved = {
      geometry: {
        type: geometry.getType(),
        coordinates: newCoords,
      },
      geometryName: geometryName,
    };
    // Translate supports single annotation only for now
    dispatch(setAnnotationsForGeometryEdit([annotationId]));
    dispatch(setNewGeometries([geometryToBeSaved]));
  });

  
  map.addInteraction(translate);
  return translate;
};

// Measure a translation offset with two clicks (from → to), fill Translate modal inputs.
const initTranslateByTwoClicksInteraction = (map, dispatch, updateAnnotationState, setTranslateOffsetDraft) => {
	let from = null;
	let firstClickLayer = null;
	// create a new vector source to store the first click
	const firstClickSource = new VectorSource();

	// trigger by turning the two-click translate on
	// used to show the first click while the tool is active
	const addFirstClickLayer = () => {
		if (firstClickLayer) return;
		// add a new vector layer to the map to display the first click
		firstClickLayer = new VectorLayer({
			source: firstClickSource,
			style: new Style({
				image: new Circle({
					radius: 6,
					fill: new Fill({ color: 'rgba(33, 84, 165, 0.35)' }),
					stroke: new Stroke({ color: '#2154A5', width: 2 }),
				}),
			}),
			zIndex: 9999,
		});
		map.addLayer(firstClickLayer);
	};

	// trigger by the second click, or by leaving the tool
	// used to drop the first-click marker
	const removeFirstClickLayer = () => {
		from = null;
		// clear the first click source
		firstClickSource.clear();
		if (!firstClickLayer) return;
		map.removeLayer(firstClickLayer);
		firstClickLayer = null;
	};

	// Hand made interaction to handle the two clicks
	// the idea is to store the first click in the vector source
	// and then when the second click is made, calculate the translation offset
	const interaction = new Interaction({
		handleEvent: (evt) => {
			if (evt.type !== 'singleclick') return true;
			const coord = map.getCoordinateFromPixel(evt.pixel);

			if (!from) {
				from = coord;
				firstClickSource.clear();
				firstClickSource.addFeature(new Feature({ geometry: new Point(coord) }));
				return false;
			}

			const [dx, dy] = openlayer_to_reference_coordinates(
				[coord[0] - from[0], coord[1] - from[1]],
				map,
			);
			// set the translation offset
			dispatch(setTranslateOffsetDraft({ x: dx, y: dy }));
			// update the annotation state to show the translate modal
			dispatch(updateAnnotationState("AnnotationsTranslateModalOnGoing"));
      // remove the first click layer
      removeFirstClickLayer();
			// return false to prevent the default behavior
			return false;
		},
	});

	// set the active state of the interaction
	const setActive = interaction.setActive.bind(interaction);
	// set the active state of the interaction to false
	interaction.setActive = (active) => {
		if (active) addFirstClickLayer();
		else removeFirstClickLayer();
		setActive(active);
	};
	map.addInteraction(interaction);
	return interaction;
};

const MIN_POINTS = { line: 2, polygon: 3 };

const samePoint = (a, b) => a[0] === b[0] && a[1] === b[1];

// corners of a line or polygon
// the point that closes a polygon is the first one repeated, so it does not count
const cornerCount = (pts) => {
	const corners = [];
	for (const p of pts) {
		if (!Array.isArray(p) || typeof p[0] !== 'number') continue;
		const prev = corners[corners.length - 1];
		if (prev && samePoint(prev, p)) continue;
		corners.push(p);
	}
	if (corners.length > 1 && samePoint(corners[0], corners[corners.length - 1])) corners.pop();
	return corners.length;
};

// trigger by the remove-points interaction, when the user finishes the box
// used to remove vertices that fall inside that box
// lines and polygons only; points stay as they are
// first and last vertices are removed too when they fall in the box
// a line keeps at least 2 points, a polygon at least 3 corners; otherwise that shape is skipped
// selected annotations only, or every line and polygon when nothing is selected
const initRemovePointsInExtentInteraction = (
	map, dispatch, setNewGeometries, setAnnotationsForGeometryEdit, annotationsRef, selectedAnnotationsRef,
) => {
	const interaction = new DragBox();
	interaction.on('boxend', () => {
    // get extent in OpenLayers coordinates
		const extentOl = interaction.getGeometry().getExtent();
    // get selected annotations ids
		const selectedIds = new Set(selectedAnnotationsRef.current);
		const updates = [];
    // loop through all annotations and check if they are a line or polygon
		for (const a of annotationsRef.current || []) {
			if (a.geometryName !== 'line' && a.geometryName !== 'polygon') continue;
			if (selectedIds.size > 0 && !selectedIds.has(a.id)) continue;
			// get the minimum number of points for the geometry type
			const min = MIN_POINTS[a.geometryName];
			// get the coordinates of the geometry
			const coords = a.geometry?.coordinates;
			if (!min || !Array.isArray(coords)) continue;
			// filter out the coordinates that are outside the extent
			const kept = coords.filter((p) =>
				!Array.isArray(p) || typeof p[0] !== 'number'
				|| !containsCoordinate(extentOl, reference_to_openlayer_coordinates(p, map))
			);
			if (kept.length === coords.length || cornerCount(kept) < min) continue;
			updates.push({ id: a.id, geometry: { ...a.geometry, coordinates: kept }, geometryName: a.geometryName });
		}
		if (!updates.length) return;
		dispatch(setAnnotationsForGeometryEdit(updates.map((u) => u.id)));
		dispatch(setNewGeometries(updates.map(({ geometry, geometryName }) => ({ geometry, geometryName }))));
	});

	const setActive = interaction.setActive.bind(interaction);
	interaction.setActive = (active) => {
		map.getInteractions().forEach((i) => {
			if (i instanceof DragPan) i.setActive(!active);
		});
		setActive(active);
	};

	map.addInteraction(interaction);
	return interaction;
};

// trigger by the text tool, on a map click
// used to open the label dialog at that point
const initTextLabelInteraction = (map, dispatch, setTextLabelDraft, textLabelDraftRef) => {
	const textLabel = new Interaction({
		handleEvent: (evt) => {
			if (evt.type !== 'singleclick') return true;
			if (textLabelDraftRef.current) return false;
			dispatch(setTextLabelDraft(openlayer_to_reference_coordinates(evt.coordinate, map)));
			return false;
		},
	});

	const setActive = textLabel.setActive.bind(textLabel);
	textLabel.setActive = (active) => {
		const viewport = map.getViewport?.();
		if (viewport) viewport.style.cursor = active ? 'crosshair' : '';
		setActive(active);
	};

	map.addInteraction(textLabel);
	return textLabel;
};
